package ai.jessie.web.profile

import ai.jessie.web.onboarding.HTTP_BAD_REQUEST
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.problem
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

data class EmailChangeUiState(
    val selfServiceUri: String? = null,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
    val notice: String? = null,
)

data class EmailChangeActions(val start: () -> Unit, val check: () -> Unit, val cancel: () -> Unit)

/**
 * UC-026 A3/A4 (approved B2). The attempt lives in server-set HttpOnly cookies, so this state holds
 * no attempt: after a reload the explicit check is still offered and the server decides whether an
 * attempt exists. [navigate] leaves Jessie for the provider's reauthentication.
 */
class EmailChangeState(
    private val port: EmailChangePort,
    private val scope: CoroutineScope,
    private val navigate: (String) -> Unit,
) {
    var state by mutableStateOf(EmailChangeUiState())
        private set

    val actions =
        EmailChangeActions(
            start = {
                request {
                    val uri = port.start().providerUri.checked()
                    currentCoroutineContext().ensureActive()
                    state = state.copy(selfServiceUri = uri)
                }
            },
            check = {
                request {
                    val uri = port.check().providerUri.checked()
                    currentCoroutineContext().ensureActive()
                    navigate(uri)
                }
            },
            cancel = {
                request {
                    port.cancel()
                    currentCoroutineContext().ensureActive()
                    state =
                        EmailChangeUiState(
                            busy = true,
                            notice =
                                "Abgebrochen. Jessie verwendet weiterhin deine bisherige " +
                                    "bestätigte E-Mail-Adresse.",
                        )
                }
            },
        )

    // Single-flight; a disposed page scope cannot publish late success, error or finalisation.
    private fun request(block: suspend () -> Unit) {
        if (state.busy) return
        state = state.copy(busy = true, error = null, notice = null)
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: ProfileRequestFailure) {
                currentCoroutineContext().ensureActive()
                state = EmailChangeUiState(busy = true, error = failure.emailProblem())
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                state = state.copy(error = unavailableProblem())
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }
}

// Only web destinations are followed or linked; anything else is treated as a failed response.
private fun String.checked() = also {
    require(it.startsWith("https://") || it.startsWith("http://"))
}

private fun ProfileRequestFailure.emailProblem() =
    when {
        status == HTTP_BAD_REQUEST && detail.code == "INVALID_ATTEMPT" ->
            OnboardingProblem(
                "Es läuft keine gültige E-Mail-Änderung. Starte die Änderung erneut. " +
                    "Deine bisherige bestätigte E-Mail-Adresse gilt weiterhin."
            )
        status == HTTP_NOT_FOUND -> OnboardingProblem("Nicht verfügbar. Es wurde nichts geändert.")
        status == HTTP_SERVICE_UNAVAILABLE ->
            OnboardingProblem(
                "Der Anmeldedienst ist vorübergehend nicht erreichbar. Es wurde nichts geändert. " +
                    "Versuche es später erneut."
            )
        else -> OnboardingRequestFailure(status, detail.code, detail.fields).problem()
    }

private const val HTTP_NOT_FOUND = 404
private const val HTTP_SERVICE_UNAVAILABLE = 503
